/** Stored as "before 21:00", "after 21:00", "exact 21:00", or "between 20:00 21:00". */

export const PRIORITY_MODES = [
  { value: 'before', label: 'Before' },
  { value: 'after', label: 'After' },
  { value: 'exact', label: 'Exact' },
  { value: 'between', label: 'Between' },
];

const clockToLabel = (hhmm) => {
  const [hStr, mStr] = String(hhmm || '').split(':');
  let hours = parseInt(hStr, 10);
  const minutes = parseInt(mStr, 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '';
  const period = hours >= 12 ? 'pm' : 'am';
  hours %= 12;
  if (hours === 0) hours = 12;
  if (minutes === 0) return `${hours} ${period}`;
  return `${hours}:${String(minutes).padStart(2, '0')} ${period}`;
};

const timeTo24 = (raw) => {
  const value = String(raw || '').trim();
  const twentyFour = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (twentyFour) {
    const hours = parseInt(twentyFour[1], 10);
    const minutes = parseInt(twentyFour[2], 10);
    if (hours > 23 || minutes > 59) return '';
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
  const twelve = value.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
  if (!twelve) return '';
  let hours = parseInt(twelve[1], 10);
  const minutes = parseInt(twelve[2] || '0', 10);
  const period = twelve[3].toLowerCase();
  if (hours < 1 || hours > 12 || minutes > 59) return '';
  if (period === 'am') {
    if (hours === 12) hours = 0;
  } else if (hours !== 12) {
    hours += 12;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const safeMode = (mode) => (PRIORITY_MODES.some((item) => item.value === mode) ? mode : 'before');

export const splitPriorityDelTime = (value) => {
  const raw = String(value || '').trim();
  const between = raw.match(/^between\s+(\S+(?:\s*[ap]m)?)(?:\s+(?:to\s+)?(\S+(?:\s*[ap]m)?))?$/i);
  if (between) {
    return { mode: 'between', time: timeTo24(between[1]), endTime: timeTo24(between[2]) };
  }
  const prefixed = raw.match(/^(before|after|exact)\s+(.+)$/i);
  if (prefixed) {
    return { mode: prefixed[1].toLowerCase(), time: timeTo24(prefixed[2]), endTime: '' };
  }
  return { mode: 'before', time: timeTo24(raw), endTime: '' };
};

export const composePriorityDelTime = (mode, time, endTime) => {
  const clock = timeTo24(time);
  if (!clock) return '';
  const safe = safeMode(mode);
  if (safe === 'between') {
    const end = timeTo24(endTime);
    return end ? `between ${clock} ${end}` : `between ${clock}`;
  }
  return `${safe} ${clock}`;
};

export const normalizePriorityDelTime = (value, { enabled = null } = {}) => {
  const raw = value == null ? '' : String(value).trim();
  if (enabled === false || raw === '') {
    return { success: true, value: '' };
  }
  const parts = splitPriorityDelTime(raw);
  if (!parts.time) {
    if (enabled === true) {
      return { success: false, value: '', msg: 'Priority delivery time is required' };
    }
    return { success: true, value: '' };
  }
  if (parts.mode === 'between') {
    if (!parts.endTime) {
      return { success: false, value: '', msg: 'Priority end time is required' };
    }
    if (parts.endTime <= parts.time) {
      return { success: false, value: '', msg: 'End time must be after start time' };
    }
  }
  return { success: true, value: composePriorityDelTime(parts.mode, parts.time, parts.endTime) };
};

export const formatPriorityTimeLabel = (value) => {
  const parts = splitPriorityDelTime(value);
  if (!parts.time) return '';
  const start = clockToLabel(parts.time);
  if (parts.mode === 'between' && parts.endTime) {
    return `Between ${start} to ${clockToLabel(parts.endTime)}`;
  }
  const mode = parts.mode === 'after' ? 'After' : parts.mode === 'exact' ? 'Exact' : 'Before';
  return `${mode} ${start}`;
};

export const formatPriorityBadgeLabel = (value) => formatPriorityTimeLabel(value) || '';

export const hasPriorityDelivery = (orderOrTime) => {
  if (!orderOrTime) return false;
  if (typeof orderOrTime === 'string') return Boolean(splitPriorityDelTime(orderOrTime).time);
  return Boolean(splitPriorityDelTime(orderOrTime.priority_del_time || orderOrTime.priorityDelTime || '').time);
};
